import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { SystemNotificationsService } from '../system-notifications.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { BehaviorSubject, Subject } from 'rxjs';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { ISystemNotificationTab } from '@fi-app/system-notifications/system-notifications.service';
import { FiStateSrefClickHandler } from '@fi-app/shared/fi-state-proxy/state-sref/state-sref.inject';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'fi-system-notifications-block',
    templateUrl: './notifications-block.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./notifications-block.component.scss'],
    host: { class: 'fi-system-notifications-block' },
    providers: [
        {
            provide: FiStateSrefClickHandler,
            useFactory: (systemNotificationsService: SystemNotificationsService) => {
                return ($event) => systemNotificationsService.close();
            },
            deps: [SystemNotificationsService],
        },
    ],
    standalone: false,
})
export class FiSystemNotificationsBlockComponent implements OnInit, OnDestroy {
    protected selectedTab: ISystemNotificationTab;
    protected tabs: ISystemNotificationTab[];
    @HostBinding('class.show-popup')
    get isOpened(): boolean {
        return this.systemNotificationsService.opened;
    }

    @HostBinding('class.closing')
    get isClosing(): boolean {
        return this.systemNotificationsService.closing;
    }

    /*@HostListener('mouseover')
    onMouseOver() {
        if (this.systemNotificationsService.closing) {
            this.systemNotificationsService.closing = false;
        }
    }*/

    constructor(
        private systemNotificationsService: SystemNotificationsService,
        private detectDevice: DetectDevicesService,
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private saveChangesWarning: SaveChangesWarningService,
    ) {}

    public ngOnInit(): void {
        this.getTabs();
        this.subscribeOnToggle();
        this.updateTabs();
        this.subscribeOnSaveChanges();
        this.subscribeOnSystemNotificationsCountChange();
    }

    private getTabs(): void {
        // switch and hide tab when the tab becomes empty (has no more notifications)
        // switch active tab to the left one. by default md-tabs shows next tab (because of selectedTabIndex
        this.systemNotificationsService.getTabs().forEach((tab) => {
            let oldValue = tab.countWatch.getValue();

            tab.countWatch.pipe(takeUntil(this.ngOnDestroy$)).subscribe((newValue) => {
                tab.$isEmpty = newValue === 0;
                this.updateTabs();

                if (newValue > oldValue) {
                    this.animateBell();
                }
                oldValue = newValue;
            });
        });
    }

    private animateBell(): void {
        // ring to bell on something new
        this.renderer.removeClass(this.elementRef.nativeElement, 'popup-was-opened');
        const bellElem = this.elementRef.nativeElement.querySelector('.animate-bell');
        if (bellElem) {
            this.renderer.removeClass(bellElem, 'animate-bell');

            setTimeout(() => {
                this.renderer.addClass(bellElem, 'animate-bell');
            }, 100);
        }
    }

    private subscribeOnToggle(): void {
        this.systemNotificationsService.onToggle.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.toggleClasses();
        });
    }

    private subscribeOnSaveChanges(): void {
        // fix for FI-1668 When clicking on a checklist in system notification dashboard
        // (seems a rendering bug in chrome)
        this.saveChangesWarning.onSaveChangesCloseConfirmed.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.renderer.addClass(this.elementRef.nativeElement, 'fix-content');
            setTimeout(() => {
                this.renderer.removeClass(this.elementRef.nativeElement, 'fix-content');
            }, 50);
        });
    }

    private subscribeOnSystemNotificationsCountChange(): void {
        // https://chainformation.atlassian.net/browse/FI-2984
        this.systemNotificationsService.systemNotificationsCount$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((cnt) => {
                if (cnt === 0) {
                    this.systemNotificationsService.close();
                }
            });
    }

    protected togglePopup(): void {
        this.systemNotificationsService.toggle();
        this.toggleClasses();
    }

    protected onSelectedTabChanged($event: ISystemNotificationTab): void {
        setTimeout(() => {
            this.selectedTab = $event;
        });
    }

    private updateTabs(): void {
        this.tabs = this.systemNotificationsService.getTabs().filter((tab) => !tab.$isEmpty);
    }

    protected setClosingClass(value: boolean): void {
        this.systemNotificationsService.closing = value;
    }

    private toggleClasses(): void {
        if (this.systemNotificationsService.opened) {
            this.renderer.addClass(this.elementRef.nativeElement, 'popup-was-opened');
        }
    }

    protected isShowTabs(): boolean {
        return this.systemNotificationsService.opened || !this.detectDevice.isMobilePhone();
    }

    protected getSystemNotificationsCountWatch(): BehaviorSubject<number> {
        return this.systemNotificationsService.systemNotificationsCount$;
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
